// Push Notification Functions (OneSignal)
// Import: import { sendTradeRequestNotification, ... } from '../lib/notifications';
//
// Every function here hands a list of app user ids to the send-push edge
// function, which resolves them to devices and talks to OneSignal.
//
// This file used to call the OneSignal REST API directly from the browser,
// which had three consequences worth remembering. The REST API key was compiled
// into the bundle and shipped inside the store binaries, so it was readable by
// anyone who unzipped the app. Correcting the key meant cutting a new release,
// because an installed copy keeps using the key it was built with. And the send
// helper only caught network exceptions, so an HTTP 403 from OneSignal was
// reported back as a success.
//
// That last point is how a total outage went unnoticed from 2026-03-17 until
// 2026-09-12: the app id was switched to the current OneSignal app without
// swapping the REST API key, every send was rejected with 403, and every caller
// logged success. Failures are now recorded server-side in
// public.push_notification_logs.

import { supabase } from './supabase';

// Sends through the edge function. Recipients are app user ids (public.users.id),
// not OneSignal subscription ids, because resolving a user to their devices
// requires reading rows the sending user does not own.
const sendPush = async (userIds, type, title, message, data = {}) => {
  const recipients = [...new Set((userIds || []).filter(Boolean))];

  if (!recipients.length) {
    console.log('[Notification] No recipients to send to');
    return { success: false, reason: 'no_recipients' };
  }

  try {
    const { data: result, error } = await supabase.functions.invoke('send-push', {
      body: { type, title, message, data, userIds: recipients },
    });

    if (error) {
      console.error('[Notification] send-push invoke failed:', error);
      return { success: false, reason: 'invoke_failed', error };
    }

    // The edge function answers 200 with an honest body, so a rejected send
    // shows up here rather than being swallowed.
    if (!result?.success) {
      console.error('[Notification] Push was not delivered:', result);
    }

    return result || { success: false, reason: 'no_response' };
  } catch (err) {
    console.error('[Notification] Send error:', err);
    return { success: false, reason: 'exception', error: err };
  }
};

// ============================================
// SECTION 1: Trade Requests
// ============================================

// 1.1 Incoming Trade Request
// Trigger: Home.js - when user sends a trade request
export const sendTradeRequestNotification = async (cardOwnerId, requesterName, cardName) => {
  return sendPush(
    [cardOwnerId],
    'trade_request',
    'New Trade Request!',
    `${requesterName} wants to trade for your ${cardName}`,
    {
      type: 'trade_request',
      route: '/requests',
      tab: 'incoming'
    }
  );
};

// 1.1b Multi-Card Trade Request
// Trigger: Home.js - when user sends trade requests for multiple cards at once
export const sendMultiTradeRequestNotification = async (cardOwnerId, requesterName, cardNames) => {
  const count = cardNames.length;
  let message;
  if (count === 1) {
    message = `${requesterName} wants to trade for your ${cardNames[0]}`;
  } else if (count === 2) {
    message = `${requesterName} wants to trade for your ${cardNames[0]} and ${cardNames[1]}`;
  } else {
    message = `${requesterName} wants to trade for ${count} of your cards: ${cardNames.slice(0, 2).join(', ')} and ${count - 2} more`;
  }

  return sendPush(
    [cardOwnerId],
    'trade_request_multi',
    count === 1 ? 'New Trade Request!' : `${count} New Trade Requests!`,
    message,
    {
      type: 'trade_request',
      route: '/requests',
      tab: 'incoming'
    }
  );
};

// 1.2 Trade Request Accepted
// Trigger: Requests.js - when owner accepts a request
export const sendTradeAcceptedNotification = async (requesterId, ownerName, cardName, tradeId) => {
  return sendPush(
    [requesterId],
    'trade_accepted',
    'Trade Request Accepted!',
    `${ownerName} accepted your request for ${cardName}`,
    {
      type: 'trade_accepted',
      route: '/chat',
      tradeId: tradeId
    }
  );
};

// ============================================
// SECTION 2: Chat Messages
// ============================================

// 2.1 New Message Received
// Trigger: Chat.js - when message is sent
export const sendNewMessageNotification = async (recipientId, senderName, messagePreview, tradeId) => {
  const preview = messagePreview.length > 50 ? messagePreview.slice(0, 50) + '...' : messagePreview;
  return sendPush(
    [recipientId],
    'new_message',
    senderName,
    preview,
    {
      type: 'new_message',
      route: '/chat',
      tradeId: tradeId
    }
  );
};

// ============================================
// SECTION 3: Post Interactions
// ============================================

// 3.1 Reaction on Post
// Trigger: Home.js - when user taps a reaction
export const sendReactionNotification = async (postOwnerId, reactorName, cardName, cardId, reactionType) => {
  // Format ball name for title
  const ballNames = {
    poke: 'Poke Ball',
    great: 'Great Ball',
    ultra: 'Ultra Ball',
    master: 'Master Ball'
  };
  const ballName = ballNames[reactionType] || 'Poke Ball';

  return sendPush(
    [postOwnerId],
    'post_reaction',
    ballName,
    `${reactorName} liked your ${cardName} post`,
    {
      type: 'post_reaction',
      route: '/card',
      cardId: cardId,
      reactorName: reactorName,
      reactionType: reactionType
    }
  );
};

// 3.2 Followed User Posted
// Trigger: Post flow - after successful post
export const sendFollowedUserPostedNotification = async (followerId, posterName, cardName, cardId, postId) => {
  return sendPush(
    [followerId],
    'followed_post',
    'New Post!',
    `${posterName} just posted ${cardName}`,
    {
      type: 'followed_post',
      route: '/home',
      cardId: cardId,
      postId: postId
    }
  );
};

// 3.3 New Follower
// Trigger: Home.js or ManageFollowing.js - when follow action
export const sendNewFollowerNotification = async (followedUserId, followerName, followerId) => {
  return sendPush(
    [followedUserId],
    'new_follower',
    'New Follower!',
    `${followerName} started following you`,
    {
      type: 'new_follower',
      route: '/user',
      userId: followerId,
      followerName: followerName
    }
  );
};

// ============================================
// SECTION 4: Trade Scheduling & Reminders
// ============================================

// 4.1 Trade Scheduled
// Trigger: Chat.js - when schedule is confirmed
export const sendTradeScheduledNotification = async (userId, partnerName, locationName, dateTime, tradeId) => {
  return sendPush(
    [userId],
    'trade_scheduled',
    'Trade Meetup Scheduled!',
    `Meet ${partnerName} at ${locationName} on ${dateTime}`,
    {
      type: 'trade_scheduled',
      route: '/trade',
      tradeId: tradeId
    }
  );
};

// 4.1b Trade Scheduled - Notify Location Staff
// Trigger: Chat.js - when schedule is confirmed at a location
export const sendLocationTradeNotification = async (locationId, trader1Name, trader2Name, dateTime) => {
  // Get all active staff for this location
  const { data: staffMembers, error } = await supabase
    .from('location_staff')
    .select('user_id')
    .eq('location_id', locationId)
    .eq('status', 'active');

  if (error || !staffMembers?.length) {
    console.log('[Notification] No staff found for location:', locationId);
    return { success: false, reason: 'no_staff' };
  }

  return sendPush(
    staffMembers.map(s => s.user_id),
    'location_trade_scheduled',
    'Trade Scheduled at Your Store!',
    `${trader1Name} & ${trader2Name} will meet on ${dateTime}`,
    {
      type: 'location_trade_scheduled',
      route: '/location-management',
    }
  );
};

// 4.2 Trade Reminder - Day Before (Edge Function)
// 4.3 Trade Reminder - Day Of (Edge Function)
// 4.4 Trade Review Prompt (Edge Function)
// These are triggered by Supabase cron, not in-app

// ============================================
// SECTION 5: Daily Summary (Edge Function)
// ============================================

// Morning digest is handled by edge function

// ============================================
// SECTION 6: Security & Account
// ============================================

// 6.1 New Device Login
// Trigger: Auth flow - on new device detected
export const sendNewDeviceAlertNotification = async (userId, deviceInfo) => {
  return sendPush(
    [userId],
    'security_alert_new_device',
    'Security Alert',
    `New login from ${deviceInfo}`,
    {
      type: 'security_alert',
      alertType: 'new_device',
      route: '/profile'
    }
  );
};

// 6.2 Password Changed
// Trigger: Profile settings - password change
export const sendPasswordChangedNotification = async (userId) => {
  return sendPush(
    [userId],
    'security_alert_password',
    'Security Alert',
    'Your password was changed',
    {
      type: 'security_alert',
      alertType: 'password_changed',
      route: '/profile'
    }
  );
};

// ============================================
// SECTION 7: Milestones & Achievements
// ============================================

// 7.1 Trade Milestone
// Trigger: Trade completion flow
export const sendMilestoneNotification = async (userId, milestoneCount) => {
  return sendPush(
    [userId],
    'milestone',
    'Milestone Reached!',
    `Congrats! You completed your ${milestoneCount}th trade!`,
    {
      type: 'milestone',
      route: '/profile',
      milestone: milestoneCount
    }
  );
};

// 7.2 Rating Warning
// Trigger: Review submission flow
export const sendRatingWarningNotification = async (userId, newRating) => {
  return sendPush(
    [userId],
    'rating_warning',
    'Rating Update',
    'Your trader rating has changed. Check your recent feedback.',
    {
      type: 'rating_warning',
      route: '/profile',
      newRating: newRating
    }
  );
};

// ============================================
// SECTION 8: Trade Cancellation & Reschedule
// ============================================

// 8.1 Trade Cancelled
export const sendTradeCancelledNotification = async (userId, cancellerName, locationName, tradeId) => {
  return sendPush([userId], 'trade_cancelled', 'Trade Cancelled',
    `@${cancellerName} cancelled the trade at ${locationName}`,
    { type: 'trade_cancelled', route: '/chat', tradeId });
};

// 8.2 Running Late
export const sendRunningLateNotification = async (userId, partnerName, minutes, tradeId) => {
  return sendPush([userId], 'running_late', 'Running Late',
    `@${partnerName} is running ${minutes} minutes late. Open the chat to respond.`,
    { type: 'running_late', route: '/chat', tradeId });
};

// 8.3 Reschedule Request
export const sendRescheduleRequestNotification = async (userId, partnerName, newDateTime, tradeId) => {
  return sendPush([userId], 'reschedule_request', 'Reschedule Request',
    `@${partnerName} wants to reschedule to ${newDateTime}. Open the chat to respond.`,
    { type: 'reschedule_request', route: '/chat', tradeId });
};

// 8.4 Reschedule Response
export const sendRescheduleResponseNotification = async (userId, partnerName, accepted, tradeId) => {
  const title = accepted ? 'Reschedule Accepted' : 'Reschedule Declined';
  const msg = accepted
    ? `@${partnerName} accepted the new time.`
    : `@${partnerName} declined the reschedule. The trade has been cancelled.`;
  return sendPush([userId], 'reschedule_response', title, msg,
    { type: 'reschedule_response', route: '/chat', tradeId });
};

// 8.5 Late Response
export const sendLateResponseNotification = async (userId, partnerName, accepted, tradeId) => {
  const title = accepted ? 'Delay Accepted' : 'Trade Cancelled';
  const msg = accepted
    ? `@${partnerName} is OK with the delay.`
    : `@${partnerName} cancelled due to the delay.`;
  return sendPush([userId], 'late_response', title, msg,
    { type: 'late_response', route: '/chat', tradeId });
};

// 8.6 Card removed from trade
export const sendCardRemovedFromTradeNotification = async (userId, ownerName, cardName, tradeId) => {
  return sendPush([userId], 'card_removed', 'Card Removed',
    `@${ownerName} removed "${cardName}" from the trade.`,
    { type: 'card_removed', route: '/chat', tradeId });
};

// 8.7 Reschedule/Late Expired (no response)
export const sendRescheduleExpiredNotification = async (userId, partnerName, tradeId) => {
  return sendPush([userId], 'reschedule_expired', 'Trade Cancelled',
    `@${partnerName} didn't respond to your request. The trade has been cancelled.`,
    { type: 'reschedule_expired', route: '/chat', tradeId });
};

// ============================================
// UTILITY: Send to multiple followers
// ============================================

// Bulk send to all followers (for "followed user posted")
export const notifyFollowers = async (posterId, cardName, cardId, postId) => {
  // Get all users who follow this poster
  const { data: followers, error } = await supabase
    .from('user_follows')
    .select('follower_id')
    .eq('following_id', posterId);

  if (error || !followers?.length) {
    console.log('[Notification] No followers to notify');
    return { success: false, reason: 'no_followers' };
  }

  // Get poster name
  const { data: poster } = await supabase
    .from('public_profiles')
    .select('username')
    .eq('id', posterId)
    .single();

  const posterName = poster?.username || 'Someone';

  // Sent as a single call rather than one per follower. The edge function
  // targets every follower's devices in one OneSignal request, which keeps a
  // popular poster from firing dozens of separate API calls.
  return sendPush(
    followers.map(f => f.follower_id),
    'followed_post',
    'New Post!',
    `${posterName} just posted ${cardName}`,
    {
      type: 'followed_post',
      route: '/home',
      cardId: cardId,
      postId: postId
    }
  );
};
